import { REQUEST_ID_HEADER, newRequestId } from '@/api/request-id'
import type { ClientCatalog, PlayResponse } from '@/features/mitra/api/frames'
// api/ reaching into panel/blocks/charts/ is the upstream direction — this
// import lived in `useTurn.ts` in `mm-ai/web` — and it is kept rather than
// inverted because the catalogue must have ONE source: the set we advertise
// on the wire is read off the same `Catalog` the renderer draws from, so the
// two cannot drift. A hand-kept list here would be the copy that lies.
import { declaredCatalog } from '@/features/mitra/panel/blocks/charts/catalog'
import type { NavFamily } from '@/components/layout/nav-items'
import {
  type EngineBearer,
  dropEngineSession,
  engineBearer,
  readyEngineBearer,
} from '@/features/mitra/api/engineSession'
import { isNotInRollout, reportNotInRollout } from '@/features/mitra/api/rollout'
import { config } from '@/lib/config'

/**
 * The only file in mm-web that knows the Mitra engine's routes, beside
 * `engineSession.ts`, which owns the one that signs the reader in.
 *
 * Deliberately NOT `src/api/client.ts`. That client is openapi-fetch bound to
 * mm-be's generated `paths` and its `{ data, meta }` envelope, and every one
 * of those assumptions is false here: the engine is a second origin, answers
 * `POST /v1/ask` with an SSE body rather than JSON, and has no OpenAPI
 * document mm-web generates from. Routing this through there would mean
 * teaching the envelope about a stream.
 *
 * IT DOES NOT BORROW THAT CLIENT'S SESSION HANDLING: nothing here refreshes
 * the mm-be session on an engine 401, for the reasons set out above `ask`.
 * The mm-be token is renewed only when its own `exp` has passed, a fact about
 * the token, never the engine's word; an engine session token the engine
 * refuses is exchanged again once (`sendWithBearer`).
 */

/** The body of `POST /v1/ask`. `AskRequest` in `mm-ai/src/mitra_ai/api/app.py`. */
export interface AskBody {
  /** 1–2000 characters, enforced server-side. */
  question: string
  /** Absent opens a new thread; the engine mints one and returns it on `ack`. */
  conversation_id?: string
  /**
   * THE IDEMPOTENCY KEY, and not the turn id. Repeated on every retry of the
   * same question so a POST whose response never arrived attaches to the turn
   * already running instead of buying a second one.
   */
  request_id: string
  /** What this build can draw. Filled in by `ask`, never by a caller. */
  catalog: ClientCatalog
  /**
   * The register the reader chose for the answer's words (engine,
   * 2026-10-13): `en`, `ne-latn` or `ne`. Absent leaves the engine to read
   * the question's own register, which is every question but a language
   * switch's re-ask.
   */
  register?: AskRegister
  /**
   * The artifact a NEW thread is opened from (`AskRequest.scope`): an "Ask
   * Mitra" entry point's company. The engine honours it only on the request
   * that opens the conversation (a company scope binds the thread's symbol,
   * `tools/resolve.scope_symbol`), so it is sent on a first question alone.
   */
  scope?: AskScope
  /**
   * The page the question was asked on (`AskRequest.page`), so "yo kina
   * ghatyo?" is about the company on screen. Unlike `scope` it binds
   * nothing: it rides EVERY question asked from a company page, or from a
   * panel opened on one, and is absent everywhere else.
   */
  page?: AskPage
  /**
   * How much the reader wants explained (`AskRequest.level`): the reader's
   * own choice in the panel, sent on every question.
   */
  level?: AskLevel
  /** The play a question was asked from (absent, never null). */
  origin?: { play: string }
  /**
   * The frame types beyond the originals this build parses, which the engine
   * sends only where they are named. Filled in by `ask` from
   * `config.mitraGenui`, and ABSENT rather than empty while it is off, so the
   * body stays the one the build before it sent.
   */
  accepts?: Accepted[]
  /**
   * The clip `POST /v1/transcribe` archived for the recording this question
   * was heard from (`clip_id`, `vc_` and 32 hex), sent back so the engine can
   * link the clip to the turn. Only on the question a transcript sent, and
   * absent, never null, on every other.
   */
  voice_clip_id?: string
}

/** `ScopeRequest`: what a thread is about, its id, and one line for the model. */
export interface AskScope {
  kind: 'company'
  ref_id: string
  summary: string
}

/**
 * `AskPage`: the page of the app a question was asked on — a company's by its
 * ticker, or any other page by its route family, the section's name and the
 * screen's own title (`lib/appPage.ts`), which only an engine stating
 * `capabilities.page_context` is sent.
 */
export type AskPage =
  { kind: 'company'; ref_id: string } | { kind: NavFamily; name: string; title?: string }

/** The levels `AskRequest.level` takes; the engine reads an absent one as `beginner`. */
export type AskLevel = 'beginner' | 'expert'

/** The registers `AskRequest.register` takes. */
export type AskRegister = 'en' | 'ne-latn' | 'ne'

/** The frame types a request may opt in to: `AskRequest.accepts`. */
type Accepted =
  'status' | 'spec' | 'stage' | 'play' | 'story' | 'pen' | 'answer_page' | 'sections' | 'watch_edit'

/** What the caller says the engine serves; each is ANDed with this build's own flag. */
export interface Negotiated {
  stage?: boolean
  play?: boolean
  story?: boolean
  pen?: boolean
  answer_page?: boolean
}

/**
 * What this build states it can parse, or nothing while the card is off.
 * `stage` only where the caller says the engine serves it AND this build's
 * flag is on (wire contract §14.1); otherwise the list is exactly the one
 * the build before the Stage sent.
 */
function accepted(n: Negotiated): { accepts: Accepted[] } | Record<string, never> {
  if (!config.mitraGenui) return {}
  // `sections` last: a deep answer laid out under a heading per part, with
  // each part's cards under its words (`section` frames and a card's `part`).
  // `watch_edit` after them: this build draws a watchlist's remove, create,
  // rename and delete cards; without it the engine proposes only an add.
  // Only the ask states it; the engine frames sections for the asker alone,
  // so a reattach or a stored read gets the same answer unsectioned.
  return { accepts: ['status', 'spec', ...negotiatedNames(n), 'sections', 'watch_edit'] }
}

/**
 * A read of stored turns (History, reattach) states what the ask did, minus
 * `status`: `spec` and the opt-ins. Empty where nothing beyond the card is
 * negotiated, so a build with every new flag off sends the request it always
 * sent.
 */
export function storedAccepts(n: Negotiated): string {
  const names = negotiatedNames(n)
  return config.mitraGenui && names.length > 0 ? ['spec', ...names].join(',') : ''
}

/** The opt-ins beyond the card itself, each ANDed with this build's flag, in the order an ask names them. */
function negotiatedNames(n: Negotiated): Accepted[] {
  const list: Accepted[] = []
  if (n.stage && config.mitraStage) list.push('stage')
  if (n.play && config.mitraPlay) list.push('play')
  if (n.story && config.mitraStory && n.stage && config.mitraStage) list.push('story')
  // The general page is a Stage, so only ever stated beside `stage`.
  if (list.includes('stage') && n.answer_page && config.mitraAnswerPage) list.push('answer_page')
  if (n.pen && config.mitraPen) list.push('pen')
  return list
}

/**
 * One question's identifier, minted by the caller and repeated on every retry
 * of it.
 *
 * Thirty-two hex characters, which is the shape `AskRequest.request_id` is
 * constrained to server-side — a client string that becomes part of a
 * database primary key is bounded at the door. That is also why
 * `newRequestId()` from `@/api/request-id` cannot stand in: it is a dashed
 * UUID, thirty-six characters, and the engine rejects it at the schema.
 *
 * It identifies the QUESTION, never the turn: the turn id stays
 * server-minted, because a client-chosen one would hand the caller the
 * namespace `/v1/turns/{id}/stream` authorises on.
 */
export function mintRequestId(): string {
  // `crypto.randomUUID` exists only in a secure context — https, or
  // localhost. Dev opened from a phone over the LAN is neither, and
  // `getRandomValues` carries no such restriction. The same fallback, for the
  // same reason, as `newRequestId` in @/api/request-id.
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID().replace(/-/g, '')
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Sends one Mitra call with the reader's bearer and the common headers.
 *
 * THE BEARER IS THE SIGNED-IN MEMBER'S OWN, never chosen by a caller: in a
 * production build an engine session token exchanged once from their mm-be
 * token (`engineSession.ts`); in staging and local builds, or on an engine
 * that offers no exchange, the mm-be token itself, renewed first when its own `exp` says it lapsed. It is read
 * fresh on every call, never captured. Signed out, no header is sent at all:
 * a literal `Bearer null` is a credential the engine then has to decide about.
 *
 * A held session token goes out in the same tick, with nothing awaited ahead
 * of the request (`useTurn.test`'s "sends the request in the same tick as the
 * ask"); only the first call of a session, or one after it lapsed, waits for
 * the exchange.
 *
 * A 401 on a session token is the engine withdrawing or expiring it: it is
 * dropped, exchanged again ONCE, and the call replayed once. That never
 * refreshes the mm-be session (see the comment above `ask` for why a second
 * origin's 401 must not), and a second 401 is handed back. A refused
 * exchange is handed back as the call's own response, with its status.
 *
 * `X-Request-ID` is mm-be's tracing convention (`@/api/request-id`), sent here
 * too so one member's failure can be found in both logs by one id. Distinct
 * from the body's `request_id`, which is idempotency, not tracing.
 */
function sendWithBearer(
  traceId: string,
  json: boolean,
  send: (headers: HeadersInit) => Promise<Response>,
): Promise<Response> {
  return watchRollout(sendAsReader(traceId, json, send))
}

/**
 * A 403 `not_in_rollout`, from the call or from the exchange in front of it,
 * shuts Mitra for this reader (`rollout.ts`), which asks `/v1/access` again.
 * The response is handed back unchanged either way; only a 403 waits on its
 * body being read.
 */
function watchRollout(sent: Promise<Response>): Promise<Response> {
  return sent.then((response) =>
    response.status !== 403
      ? response
      : isNotInRollout(response).then((refused) => {
          if (refused) reportNotInRollout()
          return response
        }),
  )
}

function sendAsReader(
  traceId: string,
  json: boolean,
  send: (headers: HeadersInit) => Promise<Response>,
): Promise<Response> {
  const go = (bearer: EngineBearer, replayed: boolean): Promise<Response> => {
    if (bearer.kind === 'refused') return Promise.resolve(refusal(bearer.status, bearer.code))
    return send(withBearer(traceId, json, bearer.token)).then((response) => {
      if (response.status !== 401 || bearer.kind !== 'session' || replayed) return response
      dropEngineSession(bearer.token)
      return engineBearer().then((again) => go(again, true))
    })
  }
  const ready = readyEngineBearer()
  return ready !== undefined ? go(ready, false) : engineBearer().then((b) => go(b, false))
}

/**
 * The exchange's refusal, standing in for the call it kept from being sent,
 * with its `code` as the engine's own refusals carry it.
 */
function refusal(status: number, code?: string): Response {
  if (code === undefined) return new Response(null, { status })
  return new Response(JSON.stringify({ code }), {
    status,
    headers: { 'content-type': 'application/json' },
  })
}

function withBearer(traceId: string, json: boolean, token: string | null): HeadersInit {
  return {
    ...(json ? { 'content-type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    [REQUEST_ID_HEADER]: traceId,
  }
}

/**
 * The one shape `encodeURIComponent` alone does not close.
 *
 * `encodeURIComponent` escapes `/`, so a turn id like `turn/../evil` reaches
 * the URL as one harmless path segment (pinned below). But `.` is not a
 * reserved character, so a turn id that is bare `.` or `..` — no slashes at
 * all — survives encoding whole and collapses the URL a segment early,
 * reaching `/v1/stream` or `/v1/cancel` directly: routes the engine does not
 * serve on their own. Upstream's `lib/turn-id.ts` closed this with a fuller
 * shape guard living in a Next.js proxy route mm-web does not have; this is
 * the same reasoning without that proxy underneath it, narrowed to the one
 * case encoding does not already handle.
 */
function isPathSegment(turnId: string): boolean {
  return turnId !== '.' && turnId !== '..'
}

/**
 * `config.mitraPrefix` is `null` when the build has no engine configured —
 * see `src/lib/config.ts`. Every function below that calls this is reachable
 * only once the panel is open, and the panel opens only through a launcher
 * gated on `config.mitraLaunchersEnabled`, which is false whenever the prefix
 * is `null`. So this throw is not a real code path today — it stands in for
 * the alternative, which is silently sending a request to `null/v1/ask`
 * (`String(null)` in a template literal) if that invariant is ever broken by
 * a future caller. Loud and immediate beats a malformed request nobody
 * notices.
 */
function requireMitraPrefix(): string {
  if (config.mitraPrefix === null) {
    throw new Error(
      'Mitra engine is not configured (VITE_MITRA_API_BASE_URL absent) — this call should be unreachable while config.mitraLaunchersEnabled is false',
    )
  }
  return config.mitraPrefix
}

function turnUrl(turnId: string, suffix: string): string {
  if (!isPathSegment(turnId)) throw new Error(`malformed turn id: ${turnId}`)
  return `${requireMitraPrefix()}/v1/turns/${encodeURIComponent(turnId)}/${suffix}`
}

/**
 * The two turn routes build their URL through `turnUrl`, which THROWS on a
 * malformed id — and both are called from async code that catches around the
 * `await` (`useTurn`'s reconnect loop, and its `cancel`). So the failure has
 * to arrive as a REJECTED PROMISE and not as a synchronous exception: a
 * caller holding only a `.catch()` would take a throw uncaught. The refresh
 * wrapper that used to sit here was async and gave that for free; removing it
 * must not quietly change the shape of this one failure.
 */
function rejecting(send: () => Promise<Response>): Promise<Response> {
  try {
    return send()
  } catch (error: unknown) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)))
  }
}

/**
 * WHY A 401 FROM THE ENGINE DOES NOT REFRESH THE mm-be SESSION.
 *
 * These routes used to run every 401 through one refresh-and-replay, on the
 * argument that a member's session is the same session whichever origin is
 * asked. That refresh was removed on the belief that an mm-be access token
 * could never verify at the engine at all — reading `mm-ai/src/mitra_ai/
 * auth/tokens.py`'s `algorithms=["RS256"]` and its per-tenant JWKS and
 * concluding that an HS256 token dies before any key material is even
 * consulted. THAT READING WAS WRONG: measured directly against the live
 * engine, an ordinary mm-be access token (HS256, SimpleJWT, `token_type:
 * access`) posted to `POST /v1/ask` gets a 200 and streams a real answer.
 * The deployed engine does accept MoneyMitra tokens.
 *
 * The actual remaining blocker to a production build ever reaching the
 * engine is CORS, not the token: the preflight to `/v1/ask` comes back 405
 * with no `Access-Control-Allow-Origin`, so a browser refuses to send the
 * request at all — there is no response for a 401 to even be read off of.
 * Dev is unaffected because the Vite proxy at `config.mitraPrefix` makes the
 * call same-origin.
 *
 * Even with that correction, refreshing on every 401 stays wrong, for the
 * two reasons below that never depended on the token-format claim:
 *
 * 1. IT CAN SIGN THE MEMBER OUT OF THE WHOLE APP. `refreshAccessToken()` is
 *    `useAuthStore.refresh()`, which calls `signOut()` on ANY non-ok refresh
 *    (`src/lib/auth/store.ts`). With an expired refresh token, one Mitra
 *    question ends the member's mm-web session — mid-page, with nothing on
 *    screen connecting the two events. A second origin's opinion of a token
 *    must not be able to do that.
 * 2. IT SCALES WITH THE FAILURE. `attachTurn` refreshes too, so a dropped
 *    turn's eight reconnect attempts are eight more refreshes for the same
 *    origin's refusal.
 *
 * So the 401 is handed straight back. `useTurn` carries its status into
 * `TurnState.terminalStatus` and the panel branches on it, which is the one
 * thing the member can act on. Whether to restore a GUARDED refresh (one that
 * cannot sign out on behalf of another origin) is a real question again now
 * that the token is verifiable — but that is a deliberate design decision
 * this comment should not make by itself, and it has not been made here.
 */

/**
 * Ask a question. The response body is SSE — `readFrames` in `./stream.ts`.
 *
 * `catalog` is set here rather than accepted from the caller, which is why
 * the parameter is `AskBody` minus that one field. The engine narrows the
 * shapes it picks to what the request declares, so a field a caller could
 * forget is a field that stops being true: a bundle one deploy behind would
 * silently receive a component it cannot draw, and A2UI's answer to an
 * unknown component is to draw nothing at all — a missing card with no error
 * beside it.
 *
 * `request_id` is the caller's, unchanged: `useTurn` mints one per QUESTION
 * and repeats it across every reconnect, so a POST whose response never
 * arrived attaches to the turn already running instead of buying a second
 * one — a second concurrency slot, a second quota hold, two completions for
 * one question.
 *
 * No `AbortSignal`: the body is a stream that legitimately stays open for the
 * length of a turn, and the reconnect loop in `useTurn` is what bounds it.
 */
export function ask(
  body: Omit<AskBody, 'catalog' | 'accepts'>,
  negotiated: Negotiated = {},
): Promise<Response> {
  const traceId = newRequestId()
  const payload = JSON.stringify({
    ...body,
    catalog: declaredCatalog(),
    ...accepted(negotiated),
  } satisfies AskBody)
  const url = `${requireMitraPrefix()}/v1/ask`
  return sendWithBearer(traceId, true, (headers) =>
    fetch(url, { method: 'POST', headers, body: payload }),
  )
}

/**
 * Re-attach to a turn already running, replaying everything after `fromSeq`
 * and then continuing live.
 *
 * The engine detaches generation from the connection that requested it, so a
 * client that drops costs a socket and never the answer. `from_seq` is the
 * cursor that makes the replay exact: restoring from zero would print the
 * text the reader already has twice, and restoring only the tail would yield
 * an answer that reads as complete and is not.
 *
 * A 401 here is handed back like any other refusal, for the reasons above
 * `ask`. It is the route that made refreshing worst: a dropped turn runs this
 * up to eight more times, so one unreachable engine became eight refreshes
 * and eight chances to sign the member out of mm-web. `useTurn` treats a
 * non-retryable status as terminal and stops, which is the honest end for a
 * reconnect that cannot be authorised.
 *
 * No `AbortSignal`, for the same reason as `ask`.
 */
export function attachTurn(
  turnId: string,
  fromSeq: number,
  { spec = config.mitraGenui }: { spec?: boolean } = {},
  extra: Negotiated = {},
): Promise<Response> {
  // What the ask stated, minus `status`: one rule for both, so a reconnect
  // mid-answer keeps the page (`stage`, `story`, `answer_page`) the ask opened.
  const accepts = spec ? ['spec', ...negotiatedNames(extra)] : []
  const traceId = newRequestId()
  return rejecting(() => {
    // A reattach states `accepts=spec` because the engine hands a later
    // subscriber a spec frame only where it says it can draw one: without it,
    // a reload mid-answer loses the card while the prose still talks about
    // it. Never `status`, which goes to no later subscriber at all. A
    // surface that draws only the card (history's replay) states it always.
    const url = `${turnUrl(turnId, 'stream')}?from_seq=${String(fromSeq)}${accepts.length > 0 ? `&accepts=${accepts.join(',')}` : ''}`
    return sendWithBearer(traceId, false, (headers) => fetch(url, { headers }))
  })
}

/**
 * Stop a turn in flight.
 *
 * The one call here that MAY be bounded, and is: it carries no stream, the
 * caller is a reader who has just pressed stop, and a cancel that hangs
 * leaves the panel unable to say whether it worked. Fifteen seconds is long
 * past any honest answer to it. A 401 is handed back unrefreshed like the
 * other two: a stop that cannot be authorised is a stop that failed, and it
 * must not take the member's mm-web session with it.
 */
export function cancelTurn(turnId: string): Promise<Response> {
  const traceId = newRequestId()
  return rejecting(() => {
    const url = turnUrl(turnId, 'cancel')
    return sendWithBearer(traceId, false, (headers) =>
      fetch(url, { method: 'POST', headers, signal: AbortSignal.timeout(15_000) }),
    )
  })
}

/**
 * One piece of an answer, read back by the engine as this reader.
 *
 * `POST /v1/speak/{turn}/{index}`, exactly as the engine's own widget calls
 * it: no body, because the engine speaks from a turn id and never from text a
 * client supplied, which is what lets it render every figure from that turn's
 * own envelopes. The reader's own bearer through `sendWithBearer`, as `ask`
 * sends it, so the voice is the answer of the person who asked it.
 *
 * A PIECE AT A TIME, which is what makes any length speakable: the whole-answer
 * route is one synthesis and refuses an answer longer than one synthesis can
 * finish. The first piece's `X-Speak-Pieces` header says how many there are.
 *
 * 425 is the one refusal worth asking again about: the engine holds a turn in
 * its registry for a moment after `done` goes out, so a request sent the
 * instant the answer lands can arrive early. Every other refusal (409 no prose
 * or no spoken form, 413, 503 no synthesiser) is the engine saying it cannot,
 * and asking again would not change that.
 */
export async function speakPiece(turnId: string, index: number): Promise<Response> {
  if (!isPathSegment(turnId)) throw new Error(`malformed turn id: ${turnId}`)
  const url = `${requireMitraPrefix()}/v1/speak/${encodeURIComponent(turnId)}/${String(index)}`
  for (let attempt = 0; ; attempt += 1) {
    const response = await sendWithBearer(newRequestId(), false, (headers) =>
      fetch(url, { method: 'POST', headers }),
    )
    if (response.status !== 425 || attempt >= 4) return response
    // Backing off rather than spinning: what this waits on is a handle being
    // dropped, which takes about as long as it takes.
    await new Promise((done) => setTimeout(done, 150 * (attempt + 1)))
  }
}

/**
 * One of the engine's reader-scoped JSON routes (`/v1/surface`, `/v1/pins`,
 * `/v1/conversations`, `/v1/turns/{id}/feedback`, `/v1/starters`,
 * `/v1/transcribe`), as the reader, with the same headers `ask` sends.
 *
 * `path` is a path this module's callers build from fixed segments and
 * `encodeURIComponent`d values, never a URL a response named. Bounded, unlike
 * `ask`: none of these carries a stream, and a shelf that hangs is a panel
 * that cannot say whether it worked. A 401 is handed back unrefreshed, for
 * the reasons set out above `ask`.
 */
export function engineFetch(
  path: string,
  init: {
    method?: string
    json?: unknown
    body?: Blob
    contentType?: string
    timeoutMs?: number
    /** Further request headers, e.g. `X-Mitra-Register` on a transcription. */
    headers?: Record<string, string>
  } = {},
): Promise<Response> {
  const { method = 'GET', json, body, contentType, timeoutMs = 15_000 } = init
  return rejecting(() => {
    const url = `${requireMitraPrefix()}${path}`
    // One deadline for the call, the exchange in front of it included.
    const signal = AbortSignal.timeout(timeoutMs)
    return sendWithBearer(newRequestId(), json !== undefined, (common) => {
      const base = { ...(common as Record<string, string>), ...init.headers }
      return fetch(url, {
        method,
        headers: contentType ? { ...base, 'content-type': contentType } : base,
        ...(json !== undefined ? { body: JSON.stringify(json) } : body ? { body } : {}),
        signal,
      })
    })
  })
}

export class PlayRefused extends Error {
  constructor(readonly status: number) {
    super(`play refused: ${String(status)}`)
  }
}

/** `POST /v1/play` (wire §15.3): no model, no quota; the response is the server's cells. */
export async function postPlay(body: {
  play_id: string
  values: Record<string, number | string>
  register?: AskRegister
}): Promise<PlayResponse> {
  const response = await engineFetch('/v1/play', { method: 'POST', json: body })
  if (!response.ok) throw new PlayRefused(response.status)
  return (await response.json()) as PlayResponse
}
